import { Coins, X } from "lucide-react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { UserAutocomplete } from "@/components/fields/user-autocomplete";
import {
  FormCurrencyInputCents,
  FormInputMask,
  FormReversedTransactionSelect,
  FormStationAutocomplete,
  FormTextarea,
  FormTransactionTypeSelect,
} from "@/components/form";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Avatar } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { FieldGroup } from "@/components/ui/field";
import { Icon } from "@/components/ui/icon";
import { SheetFooter } from "@/components/ui/sheet";
import { PhoneHelpers } from "@/lib/phone";
import type { User } from "@/models/user";
import { type TransactionFormData, TYPES_NEEDING_DIRECT_POINTS } from "../transaction-form-schema";

interface StepDetailsProps {
  onCancel: () => void;
  onContinue: () => void;
}

export function StepDetails({ onCancel, onContinue }: StepDetailsProps) {
  const { t } = useTranslation();
  const form = useFormContext<TransactionFormData>();

  const selectedUser = form.watch("user");
  const transactionType = form.watch("transaction_type");

  // Helper constants for conditional rendering
  const isPurchaseType = transactionType === "purchase";
  const isReversalType = transactionType === "reversal";
  const needsDirectPointsInput = TYPES_NEEDING_DIRECT_POINTS.includes(
    transactionType as (typeof TYPES_NEEDING_DIRECT_POINTS)[number],
  );
  const isDirectSpendingType = transactionType === "direct_spending";
  const showPurchaseInput = isPurchaseType || isDirectSpendingType;

  // Determine if transaction earns or spends points (for direct points input)
  const isEarningType = ["bonus", "manual_adjustment", "refund", "signup_bonus"].includes(
    transactionType,
  );
  const pointsPrefix = isEarningType ? "+" : "-";

  const loyaltyAccountId = selectedUser?.loyalty_account?.id ?? 0;

  const hasLoyaltyAccount = selectedUser?.loyalty_account !== undefined;

  function handleUserSelect(user: User | null) {
    form.setValue("user", user);
  }

  function handleRemoveUser() {
    form.setValue("user", null);
  }

  async function handleContinue() {
    const isValid = await form.trigger();
    if (isValid) {
      onContinue();
    }
  }

  const currentBalance = selectedUser?.loyalty_account?.current_points || 0;

  return (
    <>
      <FieldGroup>
        {/* Customer Selection */}
        <div className="space-y-2">
          <span className="text-sm font-medium">{t("transactions.create.customer")}</span>
          {selectedUser ? (
            <Card className="relative">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={handleRemoveUser}
                className="absolute top-2 right-2 size-7"
              >
                <X className="size-3.5" />
              </Button>
              <CardContent>
                <div className="flex items-center gap-3">
                  <Avatar name={selectedUser.name} className="size-10 text-sm" />
                  <div className="flex-1 space-y-0.5">
                    <h2 className="text-sm font-semibold text-foreground">
                      {selectedUser.name}
                    </h2>
                    <p className="text-xs text-muted-foreground">
                      {PhoneHelpers.format(selectedUser.phone_number)}
                    </p>
                  </div>
                  <div className="flex items-baseline gap-1">
                    <Coins className="size-3.5 text-muted-foreground" />
                    <span className="text-base font-bold tabular-nums text-foreground">
                      {currentBalance.toLocaleString()}
                    </span>
                    <span className="text-xs text-muted-foreground">{t("common.pts")}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : (
            <UserAutocomplete
              placeholder={t("transactions.create.search_customer")}
              userRole="client"
              includeLoyaltyAccount
              showPoints
              onSelect={handleUserSelect}
            />
          )}
        </div>

        {/* Station Selection */}
        <FormStationAutocomplete
          name="station"
          label={t("transactions.create.station")}
          placeholder={t("transactions.create.select_station")}
          required
        />

        {hasLoyaltyAccount && (
          <>
            {/* Transaction Type Select */}
            <FormTransactionTypeSelect
              name="transaction_type"
              label={t("transactions.create.transaction_type")}
              grouped
              required
            />

            {/* Original transaction picker — reversal only */}
            {isReversalType && (
              <FormReversedTransactionSelect
                name="reversed_transaction_id"
                loyaltyAccountId={loyaltyAccountId}
                label={t("transactions.create.original_transaction")}
                placeholder={t("transactions.create.select_original_transaction")}
                required
                onSelect={(transaction) => form.setValue("reversed_transaction", transaction)}
              />
            )}

            {/* Purchase Amount - For purchase and direct spending types */}
            {showPurchaseInput && (
              <FormCurrencyInputCents name="purchase_value_cents" label={t("transactions.create.purchase_amount")} />
            )}

            {/* Direct Points Input - For non-purchase types that need points input */}
            {needsDirectPointsInput && (
              <FormInputMask name="points" label={t("transactions.create.points")} preset="number" prefix={pointsPrefix} placeholder={t("transactions.create.enter_points_amount")} />
            )}

            {/* Description */}
            <FormTextarea
              name="description"
              label={isReversalType ? t("transactions.create.reversal_reason") : t("transactions.create.description")}
              placeholder={isReversalType ? t("transactions.create.reversal_reason_placeholder") : t("transactions.create.optional_description")}
              rows={3}
            />
          </>
        )}

        {selectedUser && !hasLoyaltyAccount && (
          <Alert variant="warning">
            <Icon name="alert-triangle" />
            <AlertTitle>{t("transactions.create.customer_problem")}</AlertTitle>
            <AlertDescription>{t("transactions.create.no_loyalty_account")}</AlertDescription>
          </Alert>
        )}
      </FieldGroup>

      <SheetFooter>
        <Button type="button" variant="outline" onClick={onCancel}>
          {t("transactions.create.cancel")}
        </Button>
        <Button type="button" onClick={handleContinue} disabled={!form.formState.isValid}>
          {t("transactions.create.continue")}
        </Button>
      </SheetFooter>
    </>
  );
}
